import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Field, FieldGrid, ToggleField } from "@/components/shared/form";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useDeleteGrade, useMaterialGrades, useUpsertGrade } from "@/features/materials/api";
import {
  emptyGradeForm,
  gradeFormSchema,
  type GradeFormValues,
} from "@/features/materials/schema";
import type { MaterialGrade } from "@/features/materials/types";

export function GradesPanel() {
  const { data = [], isLoading, error } = useMaterialGrades();
  const upsert = useUpsertGrade();
  const remove = useDeleteGrade();
  const [editing, setEditing] = useState<MaterialGrade | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<MaterialGrade | null>(null);

  const form = useForm<GradeFormValues>({
    resolver: zodResolver(gradeFormSchema),
    defaultValues: emptyGradeForm,
  });

  function openCreate() {
    setEditing(null);
    form.reset(emptyGradeForm);
    setOpen(true);
  }

  function openEdit(row: MaterialGrade) {
    setEditing(row);
    form.reset({
      name: row.name,
      description: row.description ?? "",
      available_processes: row.available_processes ?? "",
      is_active: row.is_active,
    });
    setOpen(true);
  }

  const submit = form.handleSubmit(async (values) => {
    await upsert.mutateAsync({ id: editing?.id, values });
    setOpen(false);
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Global steel grades — used on inventory and sales lines
        </p>
        <Button type="button" size="sm" onClick={openCreate}>
          <Plus />
          Add grade
        </Button>
      </div>

      {error ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {(error as Error).message}
        </p>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-2xs uppercase tracking-[0.05em] text-muted-foreground">
              <th className="px-3 py-2 font-semibold">Grade</th>
              <th className="px-3 py-2 font-semibold">Description</th>
              <th className="px-3 py-2 font-semibold">Processes</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-xs text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-xs text-muted-foreground">
                  No grades yet
                </td>
              </tr>
            ) : (
              data.map((row) => (
                <tr key={row.id} className="border-b border-border/70">
                  <td className="px-3 py-1.5 font-medium">{row.name}</td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">
                    {row.description || "—"}
                  </td>
                  <td className="max-w-[220px] truncate px-3 py-1.5 text-xs text-muted-foreground">
                    {row.available_processes || "—"}
                  </td>
                  <td className="px-3 py-1.5">
                    <StatusDot tone={row.is_active ? "success" : "neutral"}>
                      {row.is_active ? "Active" : "Inactive"}
                    </StatusDot>
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Edit ${row.name}`}
                      onClick={() => openEdit(row)}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Delete ${row.name}`}
                      className="hover:text-destructive"
                      onClick={() => setDeleting(row)}
                    >
                      <Trash2 />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit grade" : "New grade"}</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <FieldGrid>
              <Field
                label="Name"
                required
                span={6}
                error={form.formState.errors.name?.message}
                htmlFor="grade-name"
              >
                <Input id="grade-name" className="uppercase" {...form.register("name")} />
              </Field>
              <ToggleField
                label="Active"
                checked={form.watch("is_active")}
                onChange={(v) => form.setValue("is_active", v)}
                span={6}
              />
              <Field label="Description" span={12} htmlFor="grade-desc">
                <Input id="grade-desc" {...form.register("description")} />
              </Field>
              <Field
                label="Available processes"
                span={12}
                hint="Free-text hints for planning"
                htmlFor="grade-proc"
              >
                <Input id="grade-proc" {...form.register("available_processes")} />
              </Field>
            </FieldGrid>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={upsert.isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => {
          if (!o) setDeleting(null);
        }}
        title="Delete grade?"
        description={deleting ? `${deleting.name} will be removed permanently.` : undefined}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
}
